import React, { useEffect } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { mapDots, count } from "../../../core/data/auditsPage";
import { formatDate } from "../../../core/data/marketOverviewText";
import { INDUSTRY } from "../../../core/theme/auditsTheme";
import { MAP_PIN } from "../../../core/theme/searchTheme";
import { useFoldingCredit, NoLeafletPrefix } from "../../../core/layout/mapCredit";

const UK_CENTRE = [54.2, -2.5];
const UK_ZOOM = 5;
const SINGLE_PIN_ZOOM = 11;
const MAX_FIT_ZOOM = 12;
// Up to this many pins, each shows its district name like the New Search
// map; more than that, the names show on hover so they don't pile up.
const MAX_LABELLED_PINS = 15;

// Fits the map to the pins whenever they change; the whole UK when there are none.
function FitToPins({ points }) {
  const map = useMap();
  const key = points.map(p => p.join(",")).join("|");

  // The map's box is sized by the page layout, so measure again once it's on screen.
  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 0);
    return () => clearTimeout(timer);
  }, [map]);

  useEffect(() => {
    if (points.length === 0) map.setView(UK_CENTRE, UK_ZOOM);
    else if (points.length === 1) map.setView(points[0], SINGLE_PIN_ZOOM);
    else map.fitBounds(points, { padding: [24, 24], maxZoom: MAX_FIT_ZOOM });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key]);

  return null;
}

// The coverage map: the same street map as the New Search page (SearchMap),
// one pin per postcode district searched in this industry, strong for
// Repeated (searched on 2+ days) and pale for One-off. Districts with no
// position still count; a line says how many.
//   niche: the roll-up's entry, or null    message: shown instead of the map
//   highlight: the hovered Snapshot's { districts, main } (jobDistricts), or null
export default function CoverageMap({ niche, message, highlight }) {
  const { dots, offMap } = niche ? mapDots(niche.locationsCovered, niche.locations) : { dots: [], offMap: 0 };
  const labelled = dots.length <= MAX_LABELLED_PINS;
  const [creditFolded, foldCredit] = useFoldingCredit();
  const pin = INDUSTRY.pin;

  // Each pin's look: lit for the hovered Snapshot, faded for the rest.
  const lit = d => Boolean(highlight) && highlight.districts.has(d.district);
  const style = d => {
    if (lit(d)) return pin.lit;
    const base = d.repeated ? pin.repeated : pin.oneOff;
    return highlight ? { ...base, ...pin.faded } : base;
  };
  const main = highlight && dots.find(d => d.district === highlight.main);

  return (
    <div className={INDUSTRY.coverage}>
      <div className={INDUSTRY.coverageHead}>
        <h2 className={INDUSTRY.label}>Coverage</h2>
        {!message && (
          <div className={INDUSTRY.legends}>
            <span className={INDUSTRY.legend}><span aria-hidden="true" className={INDUSTRY.legendRepeated} />Repeated</span>
            <span className={INDUSTRY.legend}><span aria-hidden="true" className={INDUSTRY.legendOneOff} />One-off</span>
          </div>
        )}
      </div>
      {message ? (
        <p className={INDUSTRY.note}>{message}</p>
      ) : (
        <>
          <div className={`${INDUSTRY.map} ${creditFolded ? "map-credit-folded" : ""}`} onPointerDown={foldCredit} onWheel={foldCredit}>
            <MapContainer center={UK_CENTRE} zoom={UK_ZOOM} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
              <NoLeafletPrefix />
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {main && <CircleMarker center={[main.lat, main.lng]} pathOptions={pin.mainRing} radius={pin.mainRing.radius} interactive={false} />}
              {dots.map(d => {
                const showName = labelled || lit(d);
                // The key changes with the look so the label is redrawn (Leaflet
                // doesn't update a label's "always shown" setting in place).
                return (
                  <CircleMarker key={`${d.district}-${showName}`} center={[d.lat, d.lng]} pathOptions={style(d)} radius={style(d).radius}>
                    <Tooltip permanent={showName} direction="right" offset={[8, 0]} className={MAP_PIN.label}>
                      {showName ? d.district : `${d.district} · ${d.repeated ? "Repeated" : "One-off"} · last searched ${formatDate(d.lastSearchedAt)}`}
                    </Tooltip>
                  </CircleMarker>
                );
              })}
              <FitToPins points={dots.map(d => [d.lat, d.lng])} />
            </MapContainer>
          </div>
          {offMap > 0 && <p className={INDUSTRY.note}>{count(offMap, "location isn't", "locations aren't")} on the map.</p>}
        </>
      )}
    </div>
  );
}
